import { useState } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import styles from './ContactLookup.module.scss';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { FamilyStep, STEPS, pushFamilyStep, resetFamilyBooking, updateFBCached, updateFBStep1, updatePatients } from 'redux/familyBookingSlice';
import { ReactComponent as EmailIcon } from 'assets/icons/email_icon.svg';
import { loadOtpToken } from 'utils/storage';
import { batch } from 'react-redux';
import FormTextInput from 'components/BookingForm/FormTextInput/FormTextInput';
import PhoneInput from 'components/BookingForm/FormTextInput/PhoneInput/PhoneInput';
import GoogleButton from 'components/BookingForm/SocialButton/GoogleButton';
import FacebookButton from 'components/BookingForm/SocialButton/FacebookButton';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import { getFamilyBookingPatientsAPI } from 'services/APIs';
import { formatDobForBE } from 'components/BookingForm/utils/helperFunction';
import { UserCredential } from 'firebase/auth';
import { loginSocialThunk, updateSysType } from 'redux/authSlice';
import {
  SOCIAL_LOGIN_ERROR_MESSAGE,
  checkSocialEmail,
  getSocialUserData,
} from '../utils/socialLogin';
import MobileDatePicker from 'components/BookingForm/Step1/MobileDatePicker/MobileDatePicker';

import { renderToast } from 'components/CustomToast/CustomToast';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import cx from 'classnames';

const { VERIFICATION, SIGN_IN, SIGN_UP, CONTACT_DETAILS } = STEPS;

const ContactLookup = () => {
  const {
    familyBookingSlice: {
      step1,
      cachedData,
    },
    clinicDetailsSlice: { clinicData },
  } = useAppSelector((state) => state);
  const dispatch = useAppDispatch();
  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const [isLoading, setIsLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { control, formState, getValues, setValue } = useForm({
    mode: 'all',
    defaultValues: step1,
  });

  const getPatients = async (phoneNumber: string, dob: string) => {
    setIsLoading(true);

    const patients = await getFamilyBookingPatientsAPI(
      {
        clinicId: clinicData.id,
        dob: formatDobForBE(dob),
        phoneNumber: phoneNumber,
      },
      { otpToken: loadOtpToken(phoneNumber) }
    );

    setIsLoading(false);

    return patients;
  };

  const getNextStepForCachedData = async (phoneNumber: string, dob: string) => {
    let nextPage: FamilyStep = CONTACT_DETAILS;

    try {
      const patients = await getPatients(phoneNumber, dob);
      dispatch(updatePatients(patients.length === 0 ? [] : patients));
      dispatch(updateFBCached({ phoneNumber, dob }));
    } catch (error) {
      if (error === 'Unauthorized') {
        nextPage = VERIFICATION;
      }
    }

    return nextPage;
  };

  const handleClickOnNext = async () => {
    const phoneNumber = getValues('phoneNumber');
    const dob = getValues('dob');

    const isSamePhoneNumberAsCached = cachedData.phoneNumber === phoneNumber;

    const isSameDobAsCached = cachedData.dob === dob;

    const hasNoCachedLookup = !cachedData.dob && !cachedData.phoneNumber;

    const isPhoneNumberVerified = !!loadOtpToken(phoneNumber);

    let nextPage: FamilyStep = VERIFICATION;

    if (isSamePhoneNumberAsCached && isSameDobAsCached) {
      nextPage = CONTACT_DETAILS;
    }

    if (isSamePhoneNumberAsCached && !isSameDobAsCached) {
      nextPage = await getNextStepForCachedData(phoneNumber, dob);
    }

    if (isPhoneNumberVerified && hasNoCachedLookup) {
      nextPage = await getNextStepForCachedData(phoneNumber, dob);
    }

    batch(() => {
      dispatch(pushFamilyStep(nextPage));
      dispatch(updateFBStep1({ phoneNumber, dob }));
    });
  };

  const handleClickSignIn = () => {
    batch(() => {
      dispatch(resetFamilyBooking());
      dispatch(pushFamilyStep(SIGN_IN));
    });
  };

  const handleSocialLogin = async (
    userCredential: UserCredential,
    sysType: string
  ) => {
    try {
      setIsLoading(true);

      const { email, userToken, isExistingEmail } = await checkSocialEmail(
        userCredential,
        sysType
      );

      if (isExistingEmail) {
        const result = await dispatch(
          loginSocialThunk({ email, sysType, userToken })
        );

        if (loginSocialThunk.rejected.match(result)) {
          renderToast({ message: result.payload as string });
        }

        return;
      }

      const socialData = await getSocialUserData(
        userCredential,
        sysType,
        userToken
      );

      batch(() => {
        dispatch(updateSysType({ socialData, sysType }));
        dispatch(pushFamilyStep(SIGN_UP));
      });
    } catch (error) {
      renderToast({ message: SOCIAL_LOGIN_ERROR_MESSAGE });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <MobileDatePicker
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpdated={(newValue) => {
          setValue(
            'dob',
            `${newValue.month}/${newValue.day}/${newValue.year}`,
            { shouldValidate: true }
          );
        }}
      />
      <form noValidate className={styles['body-section']}>
        <div className={styles['form-card']}>
          <div className={styles['card-title']}>Let’s Get Started!</div>
          <div className={styles['form-content']}>
            <FormTextInput
              type="date"
              name="dob"
              label="Date of Birth"
              control={control}
              placeholder="MM/DD/YYYY"
              mask="99/99/9999"
              required="This field is required"
              rules={{
                validate: (value: string) => {
                  if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
                    return 'Invalid date of birth format';
                  }
                },
              }}
              onClick={() => setIsModalOpen(true)}
            />
            <PhoneInput
              control={control}
              countryCode={step1.countryCode}
              phoneNumber={step1.phoneNumber}
              onChangeCountryCode={(countryCode, phoneNumber) => {
                dispatch(
                  updateFBStep1({
                    countryCode,
                    phoneNumber,
                  })
                );
              }}
            />
            <div className={styles['footer-section']}>
              <div>Already have an account? Sign in to save time.</div>
              <div className={styles['social-section']}>
                <GoogleButton onSuccess={handleSocialLogin} />
                <FacebookButton onSuccess={handleSocialLogin} />
                <button
                  className={styles['click-sign-in']}
                  onClick={handleClickSignIn}
                  type="button"
                >
                  <EmailIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          className={cx(styles['actions'], {
            [styles['actions-iframe']]: isLoadedInsideIframe,
          })}
        >
          <DentalButton
            className={styles['button']}
            variant="contained"
            disabled={!formState.isValid}
            onClick={handleClickOnNext}
            isLoading={isLoading}
          >
            Next
          </DentalButton>
        </div>
      </form>
    </>
  );
};

export default ContactLookup;
